import dayjs from 'dayjs';
import { marketEventCategories } from '../../data/marketEvents';

export function nearestChartDate(target, dates) {
  if (!dates.length) return target;
  return dates.reduce((closest, date) => (
    Math.abs(dayjs(date).diff(dayjs(target), 'day')) < Math.abs(dayjs(closest).diff(dayjs(target), 'day')) ? date : closest
  ), dates[0]);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

export function eventRangeLabel(event) {
  const start = dayjs(event.start).format('DD/MM/YYYY');
  if (event.ongoing) return `${start} → in corso`;
  if (!event.end) return start;
  return `${start} → ${event.approximateEnd ? 'circa ' : ''}${dayjs(event.end).format('DD/MM/YYYY')}`;
}

export function eventTooltipHtml(event, code) {
  const category = marketEventCategories[event.category];
  return [
    `<div style="max-width:320px;white-space:normal;line-height:1.42">`,
    `<div style="font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${category.color}">${escapeHtml(code)} · ${escapeHtml(category.label)}</div>`,
    `<div style="margin-top:4px;font-size:14px;font-weight:700;color:#24191c">${escapeHtml(event.title)}</div>`,
    `<div style="margin-top:3px;font-size:11px;color:#68575c">${escapeHtml(eventRangeLabel(event))}</div>`,
    `<div style="margin-top:8px;font-size:12px;color:#24191c">${escapeHtml(event.note)}</div>`,
    `<a href="${escapeHtml(event.source.url)}" target="_blank" rel="noreferrer" style="display:inline-block;margin-top:9px;font-size:12px;font-weight:700;color:#285075;text-decoration:underline">Apri ${escapeHtml(event.source.label)} ↗</a>`,
    `</div>`,
  ].join('');
}

export function buildEventSeries(events, dates, compact = false, activeId = null) {
  if (!events.length || !dates.length) return null;
  const first = dates[0];
  const last = dates[dates.length - 1];
  const lines = [];
  const areas = [];

  events.forEach((event, index) => {
    const code = `E${index + 1}`;
    const category = marketEventCategories[event.category];
    const selected = event.id === activeId;
    const important = event.importance === 'major';
    const start = nearestChartDate(event.start < first ? first : event.start, dates);
    const rawEnd = event.end || (event.ongoing ? last : null);
    const end = rawEnd ? nearestChartDate(rawEnd > last ? last : rawEnd, dates) : null;
    const hasRange = Boolean(end && end !== start);
    const tooltip = { formatter: () => eventTooltipHtml(event, code) };

    lines.push({
      name: selected ? `${code} · ${event.shortTitle}` : code,
      xAxis: start,
      eventId: event.id,
      lineStyle: {
        color: category.color,
        width: selected ? 2.2 : important ? 1.3 : .8,
        type: selected || important ? 'solid' : 'dashed',
        opacity: selected ? .95 : important ? .58 : .14,
      },
      label: {
        show: selected && !compact,
        formatter: hasRange ? 'Inizio' : dayjs(event.start).format('DD/MM'),
        position: 'insideEndTop',
        rotate: 0,
        distance: 6,
        color: category.color,
        backgroundColor: '#fff8f9',
        borderRadius: 10,
        padding: [4, 7],
        fontFamily: 'Rubik',
        fontSize: 10,
        fontWeight: 700,
      },
      tooltip,
    });

    if (selected && hasRange) {
      lines.push({
        name: event.ongoing ? 'Ultimo dato' : 'Fine',
        xAxis: end,
        eventId: event.id,
        lineStyle: { color: category.color, width: 1.5, type: 'dashed', opacity: .82 },
        label: {
          show: !compact,
          formatter: event.ongoing ? 'Ultimo dato' : event.approximateEnd ? 'Fine ≈' : 'Fine',
          position: 'insideEndTop',
          rotate: 0,
          distance: 6,
          color: category.color,
          backgroundColor: '#fff8f9',
          borderRadius: 10,
          padding: [4, 7],
          fontFamily: 'Rubik',
          fontSize: 10,
          fontWeight: 700,
        },
        tooltip,
      });
      areas.push([
        { name: code, xAxis: start, eventId: event.id, itemStyle: { color: category.softColor }, tooltip },
        { xAxis: end, eventId: event.id, tooltip },
      ]);
    }
  });

  return {
    name: 'Eventi',
    type: 'line',
    data: dates.map(() => null),
    symbol: 'none',
    silent: false,
    z: 4,
    tooltip: { show: false },
    markLine: {
      silent: false,
      symbol: ['none', 'none'],
      animation: false,
      data: lines,
    },
    markArea: {
      silent: false,
      animation: false,
      label: { show: false },
      data: areas,
    },
  };
}

function valueAtOrBefore(target, dates, values) {
  for (let i = dates.length - 1; i >= 0; i -= 1) {
    if (dates[i] <= target && Number.isFinite(values[i])) return { date: dates[i], value: values[i] };
  }
  for (let i = 0; i < dates.length; i += 1) {
    if (Number.isFinite(values[i])) return { date: dates[i], value: values[i] };
  }
  return null;
}

function valueAtOrAfter(target, dates, values) {
  for (let i = 0; i < dates.length; i += 1) {
    if (dates[i] >= target && Number.isFinite(values[i])) return { date: dates[i], value: values[i] };
  }
  for (let i = dates.length - 1; i >= 0; i -= 1) {
    if (Number.isFinite(values[i])) return { date: dates[i], value: values[i] };
  }
  return null;
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

export function analyzeEventMovement(event, dates, analysisSeries = []) {
  if (!dates.length || !analysisSeries.length) return null;
  const lastDate = dates[dates.length - 1];
  const targetDate = event.ongoing
    ? lastDate
    : event.end || dayjs(event.start).add(7, 'day').format('YYYY-MM-DD');
  if (!event.end && !event.ongoing && targetDate > lastDate) return null;
  const isPartialRange = Boolean(event.end && event.end > lastDate);
  const movements = analysisSeries.map(series => {
    const values = series.values.map(value => value === null || value === undefined ? null : Number(value));
    const before = valueAtOrBefore(event.start, dates, values);
    const after = valueAtOrAfter(targetDate > lastDate ? lastDate : targetDate, dates, values);
    if (!before || !after || !Number.isFinite(before.value) || !Number.isFinite(after.value) || before.value === 0) return null;
    return {
      name: series.name,
      delta: after.value - before.value,
      percent: (after.value - before.value) / before.value * 100,
      from: before.date,
      to: after.date,
    };
  }).filter(Boolean);

  if (!movements.length) return null;
  const deltas = movements.map(item => item.delta);
  const percents = movements.map(item => item.percent);
  return {
    delta: median(deltas),
    percent: median(percents),
    minPercent: Math.min(...percents),
    maxPercent: Math.max(...percents),
    seriesCount: movements.length,
    from: movements[0].from,
    to: movements[0].to,
    windowLabel: event.ongoing || isPartialRange ? 'inizio → ultimo dato' : event.end ? 'inizio → fine' : 'reazione a 7 giorni',
  };
}
